/**
 * Till discounts: the per-branch policy and the shapes the till, the admin and
 * the API share. Kept free of any I/O so all three can import it.
 *
 * There are two instruments, and they are separate layers:
 *
 *  - a COUPON, which at the till is either a PRESET (a button the admin set up:
 *    "Senior citizen 20%") or a code the customer hands over. One per order.
 *    Same engine as a storefront coupon: a per-line discount on the eligible
 *    products, or on every line when the coupon applies to the whole order.
 *  - the CASHIER DISCOUNT, a percent and / or an amount the cashier types, with
 *    a reason. Applied after the coupon, capped per branch, manager PIN above
 *    the cap.
 */

/**
 * What a branch lets its cashiers do:
 *
 *  - `off`           no preset buttons and no typed discount.
 *  - `free`          presets, and a typed discount up to the branch caps
 *                    (a manager PIN above them).
 *  - `presets_only`  presets; a typed discount ALWAYS needs a manager PIN, so
 *                    a cashier on their own can only press a preset.
 *
 * A code the customer brings is not the cashier's discretion and is governed by
 * the coupon's own channel, not by this mode.
 */
export const CASHIER_DISCOUNT_MODES = ['off', 'free', 'presets_only'] as const;
export type CashierDiscountMode = (typeof CASHIER_DISCOUNT_MODES)[number];

/**
 * The mode of a branch row, read together with the boolean it replaced
 * (`cashier_discount_enabled`, which always meant "the cashier may type a
 * discount").
 *
 * The mode wins when it says `free` or `presets_only`. A row that says `off`
 * (the column's default) while the boolean is TRUE was written by something
 * that only knows the boolean: a direct insert, an import, a seed, a test. It
 * is read as `free`, which is exactly what that writer asked for. `updateBranch`
 * keeps the two in step (the boolean is true only for `free`), so a branch
 * switched off through the admin has both off and stays off.
 */
export function resolveCashierDiscountMode(
  branch:
    | { cashierDiscountMode?: string | null; cashierDiscountEnabled?: boolean | null }
    | null
    | undefined,
): CashierDiscountMode {
  const mode = branch?.cashierDiscountMode;
  if (mode === 'free' || mode === 'presets_only') return mode;
  return branch?.cashierDiscountEnabled ? 'free' : 'off';
}

/** Presets are offered in every mode but `off`. */
export function presetsOffered(mode: CashierDiscountMode): boolean {
  return mode !== 'off';
}

/** A typed discount can be entered at all (it may still need a manager PIN). */
export function typedDiscountOffered(mode: CashierDiscountMode): boolean {
  return mode !== 'off';
}

/**
 * Whether a typed discount of this size needs a manager's approval at a branch.
 * `presets_only` always does; `free` only above either cap.
 */
export function typedDiscountNeedsApproval(args: {
  mode: CashierDiscountMode;
  pct: number;
  amount: number;
  maxPct: number;
  maxAmount: number;
}): boolean {
  if (!(args.pct > 0) && !(args.amount > 0)) return false;
  if (args.mode === 'presets_only') return true;
  return args.pct > args.maxPct || args.amount > args.maxAmount;
}

/** A preset button as the till receives it. */
export type PosDiscountPreset = {
  id: string;
  name: string;
  discountType: 'percent' | 'amount';
  /** Percent (0 to 100) or pesos off each eligible unit. */
  discountValue: string;
  appliesToAll: boolean;
  /** Eligible products; empty when `appliesToAll`. */
  productIds: string[];
  requiresManagerPin: boolean;
};

/** "20% off" / "₱50.00 off each", for a button and for the admin list. */
export function describeDiscountValue(p: {
  discountType: 'percent' | 'amount';
  discountValue: string | number;
}): string {
  const n = Number(p.discountValue);
  if (p.discountType === 'percent') return `${Number.isFinite(n) ? n : 0}% off`;
  return `₱${(Number.isFinite(n) ? n : 0).toFixed(2)} off each`;
}

/**
 * Whether a preset touches anything in the cart. A preset that touches nothing
 * is greyed out on the till rather than refused after the tap.
 */
export function presetTouchesCart(
  preset: Pick<PosDiscountPreset, 'appliesToAll' | 'productIds'>,
  cartProductIds: string[],
): boolean {
  if (cartProductIds.length === 0) return false;
  if (preset.appliesToAll) return true;
  return cartProductIds.some((id) => preset.productIds.includes(id));
}
